import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import api, { Customer, VinInventoryItem } from '../lib/api'

export default function CustomerDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [item, setItem] = useState<Customer | null>(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [status, setStatus] = useState('ACTIVE')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [vinItems, setVinItems] = useState<VinInventoryItem[]>([])

  useEffect(() => {
    const run = async () => {
      setLoading(true)
      try {
        const res = await api.customers.get(id)
        const c = res.data.customer
        setItem(c)
        setName(c.name ?? '')
        setEmail(c.email ?? '')
        setPhone(c.phone ?? '')
        setStatus(c.status ?? 'ACTIVE')
        const vinResponse = await api.vinInventory.list({ customerId: id, limit: 10 })
        setVinItems(vinResponse.data)
      } catch (err: any) {
        setError(err?.response?.data?.error ?? 'Failed to load customer')
      } finally {
        setLoading(false)
      }
    }
    if (id) void run()
  }, [id])

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await api.customers.update(id, { name, email, phone, status })
      setItem(res.data.customer)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to update customer')
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!confirm('Delete this customer?')) return
    try {
      await api.customers.remove(id)
      navigate('/customers')
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to delete customer')
    }
  }

  if (loading) return <div className="max-w-3xl mx-auto p-6 text-sm text-gray-500">Loading customer...</div>

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4">
      <h1 className="text-2xl font-bold">Customer {item?.accountNumber}</h1>
      {error && <div className="border border-red-200 bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{error}</div>}
      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <input className="border rounded px-3 py-2 text-sm" value={name} onChange={(e) => setName(e.target.value)} placeholder="Customer name" />
        <select className="border rounded px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="ACTIVE">ACTIVE</option>
          <option value="INACTIVE">INACTIVE</option>
        </select>
        <input className="border rounded px-3 py-2 text-sm" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" />
        <input className="border rounded px-3 py-2 text-sm" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone" />
        <div className="md:col-span-2 flex gap-2">
          <button onClick={save} disabled={saving} className="bg-blue-700 text-white px-4 py-2 rounded text-sm">{saving ? 'Saving...' : 'Save'}</button>
          <button onClick={remove} className="border border-red-300 text-red-700 px-4 py-2 rounded text-sm">Delete</button>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-4 space-y-2">
        <h2 className="font-semibold">Recent VIN Inventory</h2>
        {!vinItems.length && <div className="text-sm text-gray-500">No VIN inventory assigned to this customer.</div>}
        {vinItems.map((vinItem) => (
          <div key={vinItem.id} className="border rounded px-3 py-2 text-sm">
            <div className="font-mono font-medium">{vinItem.vin}</div>
            <div className="text-gray-600">{[vinItem.year, vinItem.make, vinItem.model].filter(Boolean).join(' ') || '—'}</div>
            <div className="text-xs text-gray-500">{vinItem.inventoryStatus} • Decode: {vinItem.decodeStatus} • Lane: {vinItem.laneNumber || '-'}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
